import { CELL, DEBRIS, EMPTY, GRAVITY } from "../shared/defs";
import type { Letters } from "./entities";
import type { World } from "./world";

/** A flying page slab. Coordinates are world pixels; the canvas is device pixels. */
export interface Slab {
  x: number;
  y: number;
  a: number;
  vx: number;
  vy: number;
  av: number;
  poly: number[];
  canvas: HTMLCanvasElement;
  bx: number;
  by: number;
  bw: number;
  bh: number;
  colors: Uint32Array;
  ccw: number;
  cch: number;
  m: number;
  I: number;
  rest: number;
  fragment: boolean;
  age: number;
  impacts: number;
  hitFlash: number;
  ghost: boolean;
  cox?: number;
  coy?: number;
}

export interface DetachResult {
  chunks: Slab[];
  crumbs: number[];
}

function mk(w: number, h: number) {
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.floor(w));
  canvas.height = Math.max(1, Math.floor(h));
  return canvas;
}

function range(a: number, b: number) {
  return a + Math.random() * (b - a);
}

function polyArea(poly: number[]) {
  let sum = 0;
  for (let i = 0; i < poly.length; i += 2) {
    const n = (i + 2) % poly.length;
    sum += poly[i] * poly[n + 1] - poly[n] * poly[i + 1];
  }
  return sum / 2;
}

function centroid(poly: number[]): [number, number] {
  let x = 0;
  let y = 0;
  let area = 0;
  for (let i = 0; i < poly.length; i += 2) {
    const n = (i + 2) % poly.length;
    const cross = poly[i] * poly[n + 1] - poly[n] * poly[i + 1];
    area += cross;
    x += (poly[i] + poly[n]) * cross;
    y += (poly[i + 1] + poly[n + 1]) * cross;
  }
  area *= 0.5;
  if (Math.abs(area) < 0.000001) return [poly[0] ?? 0, poly[1] ?? 0];
  return [x / (6 * area), y / (6 * area)];
}

function clipHalf(poly: number[], nx: number, ny: number, offset: number) {
  const out: number[] = [];
  for (let i = 0; i < poly.length; i += 2) {
    const n = (i + 2) % poly.length;
    const x0 = poly[i];
    const y0 = poly[i + 1];
    const x1 = poly[n];
    const y1 = poly[n + 1];
    const d0 = x0 * nx + y0 * ny - offset;
    const d1 = x1 * nx + y1 * ny - offset;
    if (d0 <= 0) out.push(x0, y0);
    if (d0 <= 0 !== d1 <= 0) {
      const t = d0 / (d0 - d1);
      out.push(x0 + (x1 - x0) * t, y0 + (y1 - y0) * t);
    }
  }
  return out;
}

function insidePoly(px: number, py: number, poly: number[]) {
  for (let i = 0; i < poly.length; i += 2) {
    const n = (i + 2) % poly.length;
    const ex = poly[n] - poly[i];
    const ey = poly[n + 1] - poly[i + 1];
    if (ex * (py - poly[i + 1]) - ey * (px - poly[i]) < -0.01) return false;
  }
  return true;
}

/**
 * Rigid page slabs. A block detaches as the connected solid pixels inside it,
 * tumbles, then bakes the rotated sprite back into the page.
 */
export class Slabs {
  list: Slab[] = [];

  constructor(private world: World, private letters: Letters) {}

  detach(index: number, vx: number, vy: number, av: number, ghost = false): DetachResult {
    const out: DetachResult = { chunks: [], crumbs: [] };
    const world = this.world;
    const block = world.blocks[index];
    if (!block?.alive) return out;
    if (!ghost) {
      world.log?.block(index, vx, vy, av);
      world.log?.ctx(0);
    }
    const owned = new Set<number>();
    const stack = [index];
    while (stack.length) {
      const id = stack.pop()!;
      if (owned.has(id)) continue;
      owned.add(id);
      const child = world.blocks[id];
      if (!child) continue;
      for (const next of child.children) stack.push(next);
    }
    for (const id of owned) {
      const piece = world.blocks[id];
      if (piece) piece.alive = false;
    }
    const x0 = Math.max(0, Math.floor(block.x / CELL));
    const y0 = Math.max(0, Math.floor(block.y / CELL));
    const x1 = Math.min(world.GW - 1, Math.ceil((block.x + block.w) / CELL) - 1);
    const y1 = Math.min(world.GH - 1, Math.ceil((block.y + block.h) / CELL) - 1);
    const w = x1 - x0 + 1;
    const h = y1 - y0 + 1;
    if (w < 2 || h < 2) return out;
    const mask = new Int32Array(w * h);
    const colors = new Uint32Array(w * h);
    let count = 0;
    const spared: number[] = [];
    for (let y = y0; y <= y1; y++) {
      for (let x = x0; x <= x1; x++) {
        const cell = y * world.GW + x;
        if (world.mat[cell] === EMPTY) continue;
        const owner = world.owner[cell];
        if (owner < 0 || !owned.has(owner)) continue;
        const local = (y - y0) * w + (x - x0);
        if (world.shields(x, y)) {
          spared.push(local);
          continue;
        }
        mask[local] = 1;
        colors[local] = world.col[cell];
        count++;
        if (ghost) continue;
        world.mat[cell] = EMPTY;
        world.owner[cell] = -1;
        world.removedCells++;
        world.log?.clear(cell);
      }
    }
    if (count < 6) return out;
    const pix = world.pix;
    const stamp = mk(w, h);
    const stampCtx = stamp.getContext("2d")!;
    const stampData = stampCtx.createImageData(w, h);
    for (let i = 0; i < w * h; i++) if (mask[i]) stampData.data[i * 4 + 3] = 255;
    stampCtx.putImageData(stampData, 0, 0);
    const pw = w * pix;
    const ph = h * pix;
    const sheet = mk(pw, ph);
    const sheetCtx = sheet.getContext("2d")!;
    world.pageCache.copy(sheetCtx, x0 * pix, y0 * pix, pw, ph, 0, 0, pw, ph);
    world.textCache.copy(sheetCtx, x0 * pix, y0 * pix, pw, ph, 0, 0, pw, ph);
    for (const local of spared) {
      const lx = local % w;
      const ly = (local / w) | 0;
      sheetCtx.clearRect(lx * pix, ly * pix, pix, pix);
    }
    for (const ctx of [world.pg, world.tx]) {
      ctx.save();
      ctx.globalCompositeOperation = "destination-out";
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(stamp, x0 * pix, y0 * pix, pw, ph);
      ctx.restore();
    }
    this.letters.query(x0 * CELL, y0 * CELL, (x1 + 1) * CELL, (y1 + 1) * CELL, (i) => {
      const cx = this.letters.x[i] + this.letters.w[i] / 2;
      const cy = this.letters.y[i] + this.letters.h[i] / 2;
      const lx = Math.floor(cx / CELL) - x0;
      const ly = Math.floor(cy / CELL) - y0;
      if (!ghost && lx >= 0 && ly >= 0 && lx < w && ly < h && mask[ly * w + lx]) this.letters.remove(i);
    });
    let label = 2;
    const queue = new Int32Array(w * h);
    for (let i = 0; i < w * h; i++) {
      if (mask[i] !== 1) continue;
      const id = label++;
      let read = 0;
      let write = 0;
      queue[write++] = i;
      mask[i] = id;
      let minX = w;
      let minY = h;
      let maxX = 0;
      let maxY = 0;
      let cells = 0;
      while (read < write) {
        const at = queue[read++];
        const cx = at % w;
        const cy = (at / w) | 0;
        cells++;
        if (cx < minX) minX = cx;
        if (cx > maxX) maxX = cx;
        if (cy < minY) minY = cy;
        if (cy > maxY) maxY = cy;
        if (cx > 0 && mask[at - 1] === 1) {
          mask[at - 1] = id;
          queue[write++] = at - 1;
        }
        if (cx < w - 1 && mask[at + 1] === 1) {
          mask[at + 1] = id;
          queue[write++] = at + 1;
        }
        if (cy > 0 && mask[at - w] === 1) {
          mask[at - w] = id;
          queue[write++] = at - w;
        }
        if (cy < h - 1 && mask[at + w] === 1) {
          mask[at + w] = id;
          queue[write++] = at + w;
        }
      }
      if (cells < 24 || maxX - minX < 2 || maxY - minY < 2) {
        for (let n = 0; n < write; n++) {
          const at = queue[n];
          out.crumbs.push((x0 + (at % w) + 0.5) * CELL, (y0 + ((at / w) | 0) + 0.5) * CELL, colors[at]);
        }
        continue;
      }
      out.chunks.push(this.piece(sheet, mask, colors, w, id, minX, minY, maxX, maxY, x0, y0, vx, vy, av, ghost));
    }
    if (out.chunks.length > 1) {
      const mx = out.chunks.reduce((sum, slab) => sum + slab.x, 0) / out.chunks.length;
      const my = out.chunks.reduce((sum, slab) => sum + slab.y, 0) / out.chunks.length;
      for (const slab of out.chunks) {
        const dx = slab.x - mx;
        const dy = slab.y - my;
        const dist = Math.hypot(dx, dy) || 1;
        slab.vx += (dx / dist) * 60;
        slab.vy += (dy / dist) * 40;
        slab.av += range(-1.2, 1.2);
      }
    }
    return out;
  }

  private piece(
    sheet: HTMLCanvasElement, mask: Int32Array, colors: Uint32Array, stride: number, id: number,
    minX: number, minY: number, maxX: number, maxY: number, ox: number, oy: number,
    vx: number, vy: number, av: number, ghost: boolean,
  ) {
    const pix = this.world.pix;
    const w = maxX - minX + 1;
    const h = maxY - minY + 1;
    const stamp = mk(w, h);
    const stampCtx = stamp.getContext("2d")!;
    const data = stampCtx.createImageData(w, h);
    const local = new Uint32Array(w * h);
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const at = (y + minY) * stride + x + minX;
        if (mask[at] !== id) continue;
        data.data[(y * w + x) * 4 + 3] = 255;
        local[y * w + x] = colors[at];
      }
    }
    stampCtx.putImageData(data, 0, 0);
    const canvas = mk(w * pix, h * pix);
    const ctx = canvas.getContext("2d")!;
    ctx.drawImage(sheet, minX * pix, minY * pix, w * pix, h * pix, 0, 0, w * pix, h * pix);
    ctx.globalCompositeOperation = "destination-in";
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(stamp, 0, 0, w * pix, h * pix);
    const bw = w * CELL;
    const bh = h * CELL;
    return this.add({
      x: (ox + minX) * CELL + bw / 2,
      y: (oy + minY) * CELL + bh / 2,
      a: 0,
      vx, vy, av,
      poly: [-bw / 2, -bh / 2, bw / 2, -bh / 2, bw / 2, bh / 2, -bw / 2, bh / 2],
      canvas,
      bx: -bw / 2, by: -bh / 2, bw, bh,
      colors: local, ccw: w, cch: h,
      m: bw * bh,
      I: (bw * bh * (bw * bw + bh * bh)) / 12,
      rest: 0, fragment: false, age: 0, impacts: 0, hitFlash: 0, ghost,
    });
  }

  private add(slab: Slab) {
    this.list.push(slab);
    return slab;
  }

  shatter(slab: Slab, x: number, y: number, speed: number) {
    const at = this.list.indexOf(slab);
    if (at >= 0) this.list.splice(at, 1);
    const area = slab.bw * slab.bh;
    const n = Math.max(3, Math.min(12, Math.round(area / 1800)));
    const sites: number[] = [];
    for (let i = 0; i < n; i++) sites.push(range(slab.bx, slab.bx + slab.bw), range(slab.by, slab.by + slab.bh));
    const made: Slab[] = [];
    const cos = Math.cos(slab.a);
    const sin = Math.sin(slab.a);
    for (let i = 0; i < n; i++) {
      let poly = slab.poly.slice();
      const sx = sites[i * 2];
      const sy = sites[i * 2 + 1];
      for (let k = 0; k < n && poly.length >= 6; k++) {
        if (k === i) continue;
        const ox = sites[k * 2];
        const oy = sites[k * 2 + 1];
        poly = clipHalf(poly, ox - sx, oy - sy, (ox * ox + oy * oy - sx * sx - sy * sy) / 2);
      }
      if (poly.length < 6) continue;
      const areaPiece = Math.abs(polyArea(poly));
      if (areaPiece < 40) continue;
      const [cx, cy] = centroid(poly);
      let minX = 1e9;
      let minY = 1e9;
      let maxX = -1e9;
      let maxY = -1e9;
      for (let p = 0; p < poly.length; p += 2) {
        minX = Math.min(minX, poly[p]);
        maxX = Math.max(maxX, poly[p]);
        minY = Math.min(minY, poly[p + 1]);
        maxY = Math.max(maxY, poly[p + 1]);
      }
      const z = this.world.Z;
      const canvas = mk(Math.max(1, Math.ceil((maxX - minX) * z)), Math.max(1, Math.ceil((maxY - minY) * z)));
      const ctx = canvas.getContext("2d")!;
      ctx.setTransform(z, 0, 0, z, -minX * z, -minY * z);
      ctx.beginPath();
      ctx.moveTo(poly[0], poly[1]);
      for (let p = 2; p < poly.length; p += 2) ctx.lineTo(poly[p], poly[p + 1]);
      ctx.closePath();
      ctx.clip();
      ctx.drawImage(slab.canvas, slab.bx, slab.by, slab.bw, slab.bh);
      ctx.globalCompositeOperation = "source-atop";
      ctx.strokeStyle = "rgba(20,10,24,0.35)";
      ctx.lineWidth = 2.5;
      ctx.stroke();
      const local: number[] = [];
      for (let p = 0; p < poly.length; p += 2) local.push(poly[p] - cx, poly[p + 1] - cy);
      const wx = slab.x + cx * cos - cy * sin;
      const wy = slab.y + cx * sin + cy * cos;
      const dx = wx - x;
      const dy = wy - y;
      const dist = Math.hypot(dx, dy) || 1;
      const kick = speed * range(0.5, 1.1);
      const bw = maxX - minX;
      const bh = maxY - minY;
      const piece: Slab = {
        x: wx, y: wy, a: slab.a,
        vx: slab.vx - slab.av * (wy - slab.y) + (dx / dist) * kick,
        vy: slab.vy + slab.av * (wx - slab.x) + (dy / dist) * kick - range(40, 160),
        av: slab.av + range(-6, 6),
        poly: local, canvas,
        bx: minX - cx, by: minY - cy, bw, bh,
        colors: slab.colors, ccw: slab.ccw, cch: slab.cch,
        m: areaPiece,
        I: (areaPiece * (bw * bw + bh * bh)) / 12,
        rest: 0, fragment: true, age: 0, impacts: 0, hitFlash: 0, ghost: slab.ghost,
        cox: cx - slab.bx, coy: cy - slab.by,
      };
      this.list.push(piece);
      made.push(piece);
    }
    return made;
  }

  spall(x: number, y: number, radius: number, count: number, minR = 5, maxR = 12, ghost = false) {
    const world = this.world;
    const z = world.Z;
    for (let n = 0; n < count; n++) {
      const ang = range(0, Math.PI * 2);
      const dist = range(0, radius * 0.8);
      const px = x + Math.cos(ang) * dist;
      const py = y + Math.sin(ang) * dist;
      if (world.matAt(px, py) === EMPTY) continue;
      const rad = range(minR, maxR);
      const sides = 4 + Math.floor(Math.random() * 3);
      const poly: number[] = [];
      for (let s = 0; s < sides; s++) {
        const a = (s / sides) * Math.PI * 2 + range(-0.35, 0.35);
        const r = rad * range(0.55, 1);
        poly.push(Math.cos(a) * r, Math.sin(a) * r);
      }
      const size = Math.max(2, Math.ceil(2 * rad * z));
      const canvas = mk(size, size);
      const ctx = canvas.getContext("2d")!;
      ctx.setTransform(z, 0, 0, z, rad * z, rad * z);
      ctx.beginPath();
      ctx.moveTo(poly[0], poly[1]);
      for (let p = 2; p < poly.length; p += 2) ctx.lineTo(poly[p], poly[p + 1]);
      ctx.closePath();
      ctx.clip();
      world.pageCache.copy(ctx, (px - rad) * z, (py - rad) * z, 2 * rad * z, 2 * rad * z, -rad, -rad, 2 * rad, 2 * rad);
      ctx.globalCompositeOperation = "source-atop";
      ctx.strokeStyle = "rgba(20,10,24,0.35)";
      ctx.lineWidth = 2;
      ctx.stroke();
      const cells = Math.ceil((2 * rad) / CELL);
      const colors = new Uint32Array(cells * cells);
      for (let row = 0; row < cells; row++) {
        for (let col = 0; col < cells; col++) {
          const sx = px - rad + (col + 0.5) * CELL;
          const sy = py - rad + (row + 0.5) * CELL;
          colors[row * cells + col] = world.matAt(sx, sy) === EMPTY ? 0 : world.colorAt(sx, sy);
        }
      }
      const dx = px - x;
      const dy = py - y;
      const len = Math.hypot(dx, dy) || 1;
      const speed = range(260, 720);
      const mass = rad * rad * 2;
      this.list.push({
        x: px, y: py, a: 0,
        vx: (dx / len) * speed,
        vy: (dy / len) * speed - range(120, 300),
        av: range(-18, 18),
        poly, canvas,
        bx: -rad, by: -rad, bw: 2 * rad, bh: 2 * rad,
        colors, ccw: cells, cch: cells,
        m: mass, I: (mass * (8 * rad * rad)) / 12,
        rest: 0, fragment: true, age: 0, impacts: 0, hitFlash: 0, ghost,
      });
    }
  }

  colorAt(slab: Slab, x: number, y: number) {
    const lx = (slab.cox !== undefined ? x + slab.cox : x - slab.bx) / CELL;
    const ly = (slab.coy !== undefined ? y + slab.coy : y - slab.by) / CELL;
    const cx = Math.floor(lx);
    const cy = Math.floor(ly);
    if (cx < 0 || cy < 0 || cx >= slab.ccw || cy >= slab.cch) return 0;
    return slab.colors[cy * slab.ccw + cx] ?? 0;
  }

  update(dt: number, impact: (slab: Slab, x: number, y: number, speed: number) => void) {
    const world = this.world;
    while (this.list.length > 150) {
      const at = this.list.findIndex((slab) => slab.fragment);
      if (at < 0) break;
      if (!this.list[at].ghost) this.bake(this.list[at]);
      this.list.splice(at, 1);
    }
    for (let i = this.list.length - 1; i >= 0; i--) {
      const slab = this.list[i];
      slab.age += dt;
      slab.hitFlash = Math.max(0, slab.hitFlash - dt);
      const prevCos = Math.cos(slab.a);
      const prevSin = Math.sin(slab.a);
      const prevY = slab.y;
      slab.vy += GRAVITY * dt;
      slab.vx -= slab.vx * 0.08 * dt;
      slab.av -= slab.av * 0.15 * dt;
      slab.x += slab.vx * dt;
      slab.y += slab.vy * dt;
      slab.a += slab.av * dt;
      const cos = Math.cos(slab.a);
      const sin = Math.sin(slab.a);
      const samples: number[] = [];
      const poly = slab.poly;
      for (let p = 0; p < poly.length; p += 2) {
        const n = (p + 2) % poly.length;
        samples.push(poly[p], poly[p + 1], (poly[p] + poly[n]) / 2, (poly[p + 1] + poly[n + 1]) / 2);
      }
      let deepest = 0;
      const hits: number[] = [];
      for (let p = 0; p < samples.length; p += 2) {
        const lx = samples[p];
        const ly = samples[p + 1];
        const wx = slab.x + lx * cos - ly * sin;
        const wy = slab.y + lx * sin + ly * cos;
        const oldY = prevY + lx * prevSin + ly * prevCos;
        if (wx < 0) {
          slab.x -= wx;
          slab.vx = Math.abs(slab.vx) * 0.3;
        } else if (wx > world.W) {
          slab.x -= wx - world.W;
          slab.vx = -Math.abs(slab.vx) * 0.3;
        }
        if (wy < oldY - 0.25) continue;
        const ground = world.findSurface(wx, wx, Math.min(oldY, wy) - 1.5, wy);
        if (Number.isNaN(ground)) continue;
        const depth = wy - ground;
        hits.push(wx - slab.x, wy - slab.y);
        if (depth > deepest) deepest = depth;
      }
      if (hits.length) {
        slab.y -= deepest;
        let hitSpeed = 0;
        for (let pass = 0; pass < 2; pass++) {
          for (let h = 0; h < hits.length; h += 2) {
            const rx = hits[h];
            const ry = hits[h + 1] ?? 0;
            const vy = slab.vy + slab.av * rx;
            if (vy <= 0) continue;
            hitSpeed = Math.max(hitSpeed, vy);
            const contacts = hits.length / 2;
            const bounce = (1 + (vy > 250 ? 0.18 : 0)) * vy / (1 / slab.m + (rx * rx) / slab.I) / contacts;
            slab.vy -= bounce / slab.m;
            slab.av -= (rx * bounce) / slab.I;
            let tangent = -(slab.vx - slab.av * ry) / (1 / slab.m + (ry * ry) / slab.I) / contacts;
            const cap = 0.6 * bounce;
            tangent = Math.max(-cap, Math.min(cap, tangent));
            slab.vx += tangent / slab.m;
            slab.av -= (ry * tangent) / slab.I;
          }
        }
        if (hitSpeed > 140) {
          slab.impacts++;
          impact(slab, slab.x + (hits[0] ?? 0), slab.y + (hits[1] ?? 0), hitSpeed);
          if (!this.list.includes(slab)) continue;
        }
        const still = Math.abs(slab.vx) < 10 && Math.abs(slab.vy) < 14 && Math.abs(slab.av) < 0.25;
        slab.rest = still ? slab.rest + dt : 0;
      } else slab.rest = 0;
      if (slab.y > world.H + 400) {
        this.list.splice(i, 1);
        continue;
      }
      if (slab.rest > 0.45 || slab.age > 14) {
        if (!slab.ghost) this.bake(slab);
        this.list.splice(i, 1);
      }
    }
  }

  private bake(slab: Slab) {
    const world = this.world;
    const z = world.Z;
    const cos = Math.cos(slab.a);
    const sin = Math.sin(slab.a);
    world.pg.save();
    world.pg.setTransform(cos * z, sin * z, -sin * z, cos * z, slab.x * z, slab.y * z);
    world.pg.drawImage(slab.canvas, slab.bx, slab.by, slab.bw, slab.bh);
    world.pg.restore();
    const reach = Math.hypot(slab.bw, slab.bh) / 2;
    for (let y = Math.floor((slab.y - reach) / CELL); y <= Math.ceil((slab.y + reach) / CELL); y++) {
      for (let x = Math.floor((slab.x - reach) / CELL); x <= Math.ceil((slab.x + reach) / CELL); x++) {
        const dx = (x + 0.5) * CELL - slab.x;
        const dy = (y + 0.5) * CELL - slab.y;
        const lx = dx * cos + dy * sin;
        const ly = -dx * sin + dy * cos;
        if (!insidePoly(lx, ly, slab.poly)) continue;
        const color = this.colorAt(slab, lx, ly);
        if (color) world.markDebris(x, y, color);
      }
    }
  }

  draw(ctx: CanvasRenderingContext2D, camX: number, camY: number, scale: number) {
    for (const slab of this.list) {
      const cos = Math.cos(slab.a) * scale;
      const sin = Math.sin(slab.a) * scale;
      ctx.setTransform(cos, sin, -sin, cos, (slab.x - camX) * scale, (slab.y - camY) * scale);
      ctx.drawImage(slab.canvas, slab.bx, slab.by, slab.bw, slab.bh);
    }
    ctx.setTransform(1, 0, 0, 1, 0, 0);
  }
}

/** Segment against a slab's local box, the same test the original uses for bullets and rockets. */
export function slabAlong(slab: Slab, x0: number, y0: number, x1: number, y1: number) {
  const cos = Math.cos(slab.a);
  const sin = Math.sin(slab.a);
  const lx = (x: number, y: number) => ((x - slab.x) * cos + (y - slab.y) * sin);
  const ly = (x: number, y: number) => (-(x - slab.x) * sin + (y - slab.y) * cos);
  return segBox(lx(x0, y0), ly(x0, y0), lx(x1, y1), ly(x1, y1), slab.bx * 0.85, slab.by * 0.85, (slab.bx + slab.bw) * 0.85, (slab.by + slab.bh) * 0.85);
}

export function segBox(x0: number, y0: number, x1: number, y1: number, left: number, top: number, right: number, bottom: number) {
  let t0 = 0;
  let t1 = 1;
  const dx = x1 - x0;
  const dy = y1 - y0;
  const p = [-dx, dx, -dy, dy];
  const q = [x0 - left, right - x0, y0 - top, bottom - y0];
  for (let i = 0; i < 4; i++) {
    if (p[i] === 0) {
      if (q[i] < 0) return -1;
    } else {
      const t = q[i] / p[i];
      if (p[i] < 0) t0 = Math.max(t0, t);
      else t1 = Math.min(t1, t);
      if (t0 > t1) return -1;
    }
  }
  return t0;
}
